import { isUndefined } from 'lodash';

import { DisclaimerConfiguration } from '@/hooks/useDefaultDisclaimers';
import { TenantPresentationConfiguration_SubBrandDisclaimerConfigurationFragment } from '@/modules/presentation/graphql/TenantPresentationConfiguration.generated';
import {
  AugmentedCreateOrganizationDisclaimerConfigurationInput,
  AugmentedCreateSubBrandDisclaimerConfigurationInput,
  AugmentedUpdateOrganizationDisclaimerConfigurationInput,
  AugmentedUpdateSubBrandDisclaimerConfigurationInput,
  AugmentedUpdateTenantDisclaimerConfigurationInput,
  CreateOrganizationDisclaimerConfigurationInput,
  CreateSubBrandDisclaimerConfigurationInput,
  UpdateOrganizationDisclaimerConfigurationInput,
} from '@/types/schema';
import { PathsOf } from '@/types/subform';
import { getValueOrClear } from '@/utils/graphqlFormUtils';

import {
  LegalDisclaimersForm_OrganizationDisclaimerConfigurationFragment,
  LegalDisclaimersForm_TenantDisclaimerConfigurationFragment,
} from './graphql/AdminLegalDisclaimers.generated';
import { LegalDisclaimersFormShape } from './LegalDisclaimersForm';

export function getFormDisclaimers(
  defaultDisclaimerConfiguration: DisclaimerConfiguration,
  tenantDisclosures?:
    | LegalDisclaimersForm_TenantDisclaimerConfigurationFragment
    | LegalDisclaimersForm_OrganizationDisclaimerConfigurationFragment
    | null
    | undefined,
  subBrandDisclosures?:
    | TenantPresentationConfiguration_SubBrandDisclaimerConfigurationFragment
    | null
    | undefined
): LegalDisclaimersFormShape {
  const output: LegalDisclaimersFormShape = {
    presentationCoverDisclaimerBody:
      subBrandDisclosures?.presentationCoverDisclaimerBody ??
      tenantDisclosures?.presentationCoverDisclaimerBody ??
      defaultDisclaimerConfiguration.presentationCoverDisclaimerBody,
    presentationCoverDisclaimerTitle:
      subBrandDisclosures?.presentationCoverDisclaimerTitle ??
      tenantDisclosures?.presentationCoverDisclaimerTitle ??
      defaultDisclaimerConfiguration.presentationCoverDisclaimerTitle,
    presentationFooterDisclaimerTitle:
      subBrandDisclosures?.presentationFooterDisclaimerTitle ??
      tenantDisclosures?.presentationFooterDisclaimerTitle ??
      defaultDisclaimerConfiguration.presentationFooterDisclaimerTitle,
    presentationFooterDisclaimerBody:
      subBrandDisclosures?.presentationFooterDisclaimerBody ??
      tenantDisclosures?.presentationFooterDisclaimerBody ??
      defaultDisclaimerConfiguration.presentationFooterDisclaimerBody,
    presentationStandaloneDisclaimerTitle:
      subBrandDisclosures?.presentationStandaloneDisclaimerTitle ??
      tenantDisclosures?.presentationStandaloneDisclaimerTitle ??
      defaultDisclaimerConfiguration.presentationStandaloneDisclaimerTitle,
    presentationStandaloneDisclaimerBody:
      subBrandDisclosures?.presentationStandaloneDisclaimerBody ??
      tenantDisclosures?.presentationStandaloneDisclaimerBody ??
      defaultDisclaimerConfiguration.presentationStandaloneDisclaimerBody,
    includeLuminaryDisclaimer:
      subBrandDisclosures?.includeLuminaryDisclaimer ??
      tenantDisclosures?.includeLuminaryDisclaimer ??
      false,
  };

  return output;
}

/**
 * @description Checks if the value is different from the default value or the tenant value, as only those get
 * submitted
 * @param value Current value
 * @param defaultValue Default value
 * @param tenantValue Tenant value
 * @returns the form value,  or null if the value is the same as the default value or the tenant value
 */
function checkField(
  value: string,
  defaultValue: string,
  tenantValue?: string | null | undefined
): string | null {
  if (value === defaultValue) return null;
  if (!isUndefined(tenantValue) && value === tenantValue) return null;
  return value;
}

export function getUpdateTenantDisclaimersInput(
  disclaimerId: string,
  formValues: LegalDisclaimersFormShape,
  defaultDisclaimerConfiguration: DisclaimerConfiguration
): AugmentedUpdateTenantDisclaimerConfigurationInput {
  return {
    id: disclaimerId,
    update: getOrganizationDisclaimerFields(
      formValues,
      defaultDisclaimerConfiguration
    ),
  };
}

export function getCreateOrganizationDisclaimersInput(
  formValues: LegalDisclaimersFormShape,
  defaultDisclaimerConfiguration: DisclaimerConfiguration
): AugmentedCreateOrganizationDisclaimerConfigurationInput {
  const create: CreateOrganizationDisclaimerConfigurationInput = {
    includeLuminaryDisclaimer: formValues.includeLuminaryDisclaimer,
  };

  if (
    checkField(
      formValues.presentationCoverDisclaimerTitle,
      defaultDisclaimerConfiguration.presentationCoverDisclaimerTitle
    )
  ) {
    create.presentationCoverDisclaimerTitle =
      formValues.presentationCoverDisclaimerTitle;
  }
  if (
    checkField(
      formValues.presentationCoverDisclaimerBody,
      defaultDisclaimerConfiguration.presentationCoverDisclaimerBody
    )
  ) {
    create.presentationCoverDisclaimerBody =
      formValues.presentationCoverDisclaimerBody;
  }
  if (
    checkField(
      formValues.presentationFooterDisclaimerTitle,
      defaultDisclaimerConfiguration.presentationFooterDisclaimerTitle
    )
  ) {
    create.presentationFooterDisclaimerTitle =
      formValues.presentationFooterDisclaimerTitle;
  }
  if (
    checkField(
      formValues.presentationFooterDisclaimerBody,
      defaultDisclaimerConfiguration.presentationFooterDisclaimerBody
    )
  ) {
    create.presentationFooterDisclaimerBody =
      formValues.presentationFooterDisclaimerBody;
  }
  if (
    checkField(
      formValues.presentationStandaloneDisclaimerTitle,
      defaultDisclaimerConfiguration.presentationStandaloneDisclaimerTitle
    )
  ) {
    create.presentationStandaloneDisclaimerTitle =
      formValues.presentationStandaloneDisclaimerTitle;
  }
  if (
    checkField(
      formValues.presentationStandaloneDisclaimerBody,
      defaultDisclaimerConfiguration.presentationStandaloneDisclaimerBody
    )
  ) {
    create.presentationStandaloneDisclaimerBody =
      formValues.presentationStandaloneDisclaimerBody;
  }

  return { create };
}

export function getUpdateOrganizationDisclaimersInput(
  disclaimerId: string,
  formValues: LegalDisclaimersFormShape,
  defaultDisclaimerConfiguration: DisclaimerConfiguration
): AugmentedUpdateOrganizationDisclaimerConfigurationInput {
  return {
    id: disclaimerId,
    update: getOrganizationDisclaimerFields(
      formValues,
      defaultDisclaimerConfiguration
    ),
  };
}

function getOrganizationDisclaimerFields(
  formValues: LegalDisclaimersFormShape,
  defaultDisclaimerConfiguration: DisclaimerConfiguration
): UpdateOrganizationDisclaimerConfigurationInput {
  const fields: Record<
    PathsOf<Omit<LegalDisclaimersFormShape, 'includeLuminaryDisclaimer'>>,
    string | null
  > = {
    presentationCoverDisclaimerTitle: checkField(
      formValues.presentationCoverDisclaimerTitle,
      defaultDisclaimerConfiguration.presentationCoverDisclaimerTitle
    ),
    presentationCoverDisclaimerBody: checkField(
      formValues.presentationCoverDisclaimerBody,
      defaultDisclaimerConfiguration.presentationCoverDisclaimerBody
    ),
    presentationFooterDisclaimerTitle: checkField(
      formValues.presentationFooterDisclaimerTitle,
      defaultDisclaimerConfiguration.presentationFooterDisclaimerTitle
    ),
    presentationFooterDisclaimerBody: checkField(
      formValues.presentationFooterDisclaimerBody,
      defaultDisclaimerConfiguration.presentationFooterDisclaimerBody
    ),
    presentationStandaloneDisclaimerTitle: checkField(
      formValues.presentationStandaloneDisclaimerTitle,
      defaultDisclaimerConfiguration.presentationStandaloneDisclaimerTitle
    ),
    presentationStandaloneDisclaimerBody: checkField(
      formValues.presentationStandaloneDisclaimerBody,
      defaultDisclaimerConfiguration.presentationStandaloneDisclaimerBody
    ),
  };

  return {
    ...getValueOrClear(
      `presentationCoverDisclaimerBody`,
      fields.presentationCoverDisclaimerBody
    ),
    ...getValueOrClear(
      `presentationCoverDisclaimerTitle`,
      fields.presentationCoverDisclaimerTitle
    ),
    ...getValueOrClear(
      `presentationFooterDisclaimerTitle`,
      fields.presentationFooterDisclaimerTitle
    ),
    ...getValueOrClear(
      `presentationFooterDisclaimerBody`,
      fields.presentationFooterDisclaimerBody
    ),
    ...getValueOrClear(
      `presentationStandaloneDisclaimerTitle`,
      fields.presentationStandaloneDisclaimerTitle
    ),
    ...getValueOrClear(
      `presentationStandaloneDisclaimerBody`,
      fields.presentationStandaloneDisclaimerBody
    ),
    includeLuminaryDisclaimer: formValues.includeLuminaryDisclaimer,
  };
}

type TenantDisclosures =
  | LegalDisclaimersForm_TenantDisclaimerConfigurationFragment
  | null
  | undefined;
export function getUpdateSubBrandDisclaimersInput(
  disclaimerId: string,
  formValues: LegalDisclaimersFormShape,
  tenantDisclosures: TenantDisclosures,
  defaultDisclaimerConfiguration: DisclaimerConfiguration
): AugmentedUpdateSubBrandDisclaimerConfigurationInput {
  const fields: Record<
    PathsOf<Omit<LegalDisclaimersFormShape, 'includeLuminaryDisclaimer'>>,
    string | boolean | null
  > = {
    presentationCoverDisclaimerTitle: checkField(
      formValues.presentationCoverDisclaimerTitle,
      defaultDisclaimerConfiguration.presentationCoverDisclaimerTitle,
      tenantDisclosures?.presentationCoverDisclaimerTitle
    ),
    presentationCoverDisclaimerBody: checkField(
      formValues.presentationCoverDisclaimerBody,
      defaultDisclaimerConfiguration.presentationCoverDisclaimerBody,
      tenantDisclosures?.presentationCoverDisclaimerBody
    ),
    presentationFooterDisclaimerTitle: checkField(
      formValues.presentationFooterDisclaimerTitle,
      defaultDisclaimerConfiguration.presentationFooterDisclaimerTitle,
      tenantDisclosures?.presentationFooterDisclaimerTitle
    ),
    presentationFooterDisclaimerBody: checkField(
      formValues.presentationFooterDisclaimerBody,
      defaultDisclaimerConfiguration.presentationFooterDisclaimerBody,
      tenantDisclosures?.presentationFooterDisclaimerBody
    ),
    presentationStandaloneDisclaimerTitle: checkField(
      formValues.presentationStandaloneDisclaimerTitle,
      defaultDisclaimerConfiguration.presentationStandaloneDisclaimerTitle,
      tenantDisclosures?.presentationStandaloneDisclaimerTitle
    ),
    presentationStandaloneDisclaimerBody: checkField(
      formValues.presentationStandaloneDisclaimerBody,
      defaultDisclaimerConfiguration.presentationStandaloneDisclaimerBody,
      tenantDisclosures?.presentationStandaloneDisclaimerBody
    ),
  };

  const output: AugmentedUpdateSubBrandDisclaimerConfigurationInput = {
    id: disclaimerId,
    update: {
      ...getValueOrClear(
        `presentationCoverDisclaimerTitle`,
        fields.presentationCoverDisclaimerTitle
      ),
      ...getValueOrClear(
        `presentationCoverDisclaimerBody`,
        fields.presentationCoverDisclaimerBody
      ),
      ...getValueOrClear(
        `presentationFooterDisclaimerTitle`,
        fields.presentationFooterDisclaimerTitle
      ),
      ...getValueOrClear(
        `presentationFooterDisclaimerBody`,
        fields.presentationFooterDisclaimerBody
      ),
      ...getValueOrClear(
        `presentationStandaloneDisclaimerTitle`,
        fields.presentationStandaloneDisclaimerTitle
      ),
      ...getValueOrClear(
        `presentationStandaloneDisclaimerBody`,
        fields.presentationStandaloneDisclaimerBody
      ),
      includeLuminaryDisclaimer: formValues.includeLuminaryDisclaimer,
    },
  };
  return output;
}

export function getCreateSubBrandDisclaimersInput(
  formData: LegalDisclaimersFormShape,
  subBrandId: string,
  tenantDisclosures: TenantDisclosures,
  defaultDisclaimerConfiguration: DisclaimerConfiguration
): AugmentedCreateSubBrandDisclaimerConfigurationInput {
  const create: CreateSubBrandDisclaimerConfigurationInput = {
    subBrandID: subBrandId,
    includeLuminaryDisclaimer: formData.includeLuminaryDisclaimer,
  };

  if (
    checkField(
      formData.presentationCoverDisclaimerTitle,
      defaultDisclaimerConfiguration.presentationCoverDisclaimerTitle,
      tenantDisclosures?.presentationCoverDisclaimerTitle
    )
  ) {
    create.presentationCoverDisclaimerTitle =
      formData.presentationCoverDisclaimerTitle;
  }
  if (
    checkField(
      formData.presentationCoverDisclaimerBody,
      defaultDisclaimerConfiguration.presentationCoverDisclaimerBody,
      tenantDisclosures?.presentationCoverDisclaimerBody
    )
  ) {
    create.presentationCoverDisclaimerBody =
      formData.presentationCoverDisclaimerBody;
  }
  if (
    checkField(
      formData.presentationFooterDisclaimerTitle,
      defaultDisclaimerConfiguration.presentationFooterDisclaimerTitle,
      tenantDisclosures?.presentationFooterDisclaimerTitle
    )
  ) {
    create.presentationFooterDisclaimerTitle =
      formData.presentationFooterDisclaimerTitle;
  }
  if (
    checkField(
      formData.presentationFooterDisclaimerBody,
      defaultDisclaimerConfiguration.presentationFooterDisclaimerBody,
      tenantDisclosures?.presentationFooterDisclaimerBody
    )
  ) {
    create.presentationFooterDisclaimerBody =
      formData.presentationFooterDisclaimerBody;
  }
  if (
    checkField(
      formData.presentationStandaloneDisclaimerTitle,
      defaultDisclaimerConfiguration.presentationStandaloneDisclaimerTitle,
      tenantDisclosures?.presentationStandaloneDisclaimerTitle
    )
  ) {
    create.presentationStandaloneDisclaimerTitle =
      formData.presentationStandaloneDisclaimerTitle;
  }
  if (
    checkField(
      formData.presentationStandaloneDisclaimerBody,
      defaultDisclaimerConfiguration.presentationStandaloneDisclaimerBody,
      tenantDisclosures?.presentationStandaloneDisclaimerBody
    )
  ) {
    create.presentationStandaloneDisclaimerBody =
      formData.presentationStandaloneDisclaimerBody;
  }
  return {
    create,
  };
}
